import { useState } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { Input } from 'src/components/Input';
import { Button } from 'src/components/Button';
import { Heading } from 'src/components/Heading';
import { DraggableCrmField } from '../DraggableCrmField';
import { DropZone } from '../DropZone';
import { useCrmSync } from '../../contexts/CrmSyncContext';
import { useFieldMapping } from '../../contexts/FieldMappingContext';
import { useContacts } from '../../contexts/ContactsContext';
import { useNotification } from 'src/contexts/NotificationContext';

export const FieldMapping = () => {
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const notification = useNotification();

  const {
    integratedCrm,
    additionalParams,
    syncInProgress,
    handleStepNavigation,
  } = useCrmSync();

  const {
    isFieldMappingValid,
    availableCrmFields,
    mappingEntries,
    isInitialized,
    searchQuery,
    setSearchQuery,
    sampleContacts,
    filteredCrmFields,
    requiredFields,
    optionalFields,
    updateFieldMapping,
  } = useFieldMapping();

  const { handlePreviewUpload } = useContacts();

  const handleBack = () => handleStepNavigation('back');
  const handleContinue = async () => {
    try {
      setIsLoadingPreview(true);
      await handlePreviewUpload(
        integratedCrm,
        additionalParams,
        handleStepNavigation,
        notification,
      );
    } catch (error) {
      // Error handling is already done in handlePreviewUpload
      console.error('Preview upload failed:', error);
    } finally {
      setIsLoadingPreview(false);
    }
  };
  const disabled = syncInProgress;

  const handleFieldMapped = (campaignField: string, crmFieldValue: string) => {
    updateFieldMapping(campaignField, crmFieldValue);
  };

  const handleFieldRemoved = (campaignField: string) => {
    updateFieldMapping(campaignField, null);
  };

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-neutral-500">Loading field mapping...</div>
      </div>
    );
  }

  return (
    <>
      <Heading
        title="Map schema"
        subtitle="Map columns from your CRM to Fluents properties"
        backAction={handleBack}
      >
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outlined"
            onClick={handleBack}
            disabled={disabled}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleContinue}
            disabled={disabled || !isFieldMappingValid || isLoadingPreview}
          >
            {isLoadingPreview ? 'Loading Preview...' : 'Preview Upload'}
          </Button>
        </div>
      </Heading>

      <div className="max-w-6xl">
        <div className="grid grid-cols-[350px_1fr] gap-8 h-full">
          {/* Left Column - CRM Fields */}
          <div className="bg-neutral-50 p-6 h-full">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                  CRM fields
                </h3>
                <Input
                  type="text"
                  placeholder="Search fields"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  icon={<MagnifyingGlass size={20} />}
                  iconPosition="left"
                />
              </div>

              <div
                className="space-y-2 overflow-y-auto max-h-[100vh]"
                onDragOver={(e) => {
                  e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                }}
              >
                {filteredCrmFields.map((field) => (
                  <DraggableCrmField
                    key={field.value}
                    field={field}
                    sampleContacts={sampleContacts}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Fluents Properties */}
          <div className="space-y-4 py-6">
            {/* Required Properties */}
            <div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                Required properties
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {requiredFields.map((field) => {
                  const mapping = mappingEntries.find(
                    (m) => m.campaignField === field.name,
                  );
                  const mappedField = mapping?.crmField
                    ? availableCrmFields.find(
                        (f) => f.value === mapping.crmField,
                      )
                    : null;

                  return (
                    <DropZone
                      key={field.name}
                      campaignField={field.name}
                      fieldLabel={field.label}
                      mappedField={mappedField ?? null}
                      sampleContacts={sampleContacts}
                      required={field.required}
                      onFieldMapped={handleFieldMapped}
                      onFieldRemoved={handleFieldRemoved}
                    />
                  );
                })}
              </div>
            </div>

            {/* Optional Properties */}
            {optionalFields.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                  Optional properties
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {optionalFields.map((field) => {
                    const mapping = mappingEntries.find(
                      (m) => m.campaignField === field.name,
                    );
                    const mappedField = mapping?.crmField
                      ? availableCrmFields.find(
                          (f) => f.value === mapping.crmField,
                        )
                      : null;

                    return (
                      <DropZone
                        key={field.name}
                        campaignField={field.name}
                        fieldLabel={field.label}
                        mappedField={mappedField ?? null}
                        disabled={disabled}
                        sampleContacts={sampleContacts}
                        required={field.required}
                        onFieldMapped={handleFieldMapped}
                        onFieldRemoved={handleFieldRemoved}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
